import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Copy, Check, ExternalLink, Loader2, DollarSign, TrendingUp, Link2, QrCode, Download, Sparkles,
} from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PayPalLogo, VenmoLogo, AppleLogo } from "@/components/PaymentIcons";
import { PayoutCard } from "@/components/PayoutCard";
import { ConnectCard } from "@/components/ConnectCard";
import { PushNotifyCard } from "@/components/PushNotifyCard";

export default function Dashboard() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const qrRef = useRef(null);
  const [replyDrafts, setReplyDrafts] = useState({});

  const loadEarnings = () =>
    api.get("/earnings").then(({ data }) => setEarnings(data)).catch(() => {});

  useEffect(() => {
    if (user && user !== false) {
      setForm({
        name: user.name || "",
        bio: user.bio || "",
        job_title: user.job_title || "",
        avatar_url: user.avatar_url || "",
        phone: user.phone || "",
        public_wall: user.public_wall !== false,
        handles: {
          paypal: user.handles?.paypal || "",
          venmo: user.handles?.venmo || "",
          applepay: user.handles?.applepay || "",
        },
      });
      loadEarnings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const submitReply = async (tipId) => {
    const reply = (replyDrafts[tipId] || "").trim();
    try {
      await api.post(`/tips/${tipId}/reply`, { reply });
      toast.success("Reply saved");
      setReplyDrafts((d) => ({ ...d, [tipId]: undefined }));
      loadEarnings();
    } catch {
      toast.error("Could not save reply");
    }
  };

  const [suggesting, setSuggesting] = useState(null);
  const suggestReply = async (tipId) => {
    setSuggesting(tipId);
    try {
      const { data } = await api.post(`/tips/${tipId}/suggest-reply`);
      setReplyDrafts((d) => ({ ...d, [tipId]: data.suggestion }));
    } catch {
      toast.error("Could not generate a suggestion");
    } finally {
      setSuggesting(null);
    }
  };

  if (user === null || !form)
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#050505]">
        <Loader2 className="h-8 w-8 animate-spin text-[#00E676]" />
      </div>
    );

  const tipUrl = `${window.location.origin}/u/${user.username}`;

  const copyLink = async () => {
    await navigator.clipboard.writeText(tipUrl);
    setCopied(true);
    toast.success("Tip link copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/profile", form);
      setUser(data);
      toast.success("Profile saved");
    } catch {
      toast.error("Could not save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const setHandle = (k) => (e) =>
    setForm({ ...form, handles: { ...form.handles, [k]: e.target.value } });

  const downloadQr = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `tip-${user.username}-qr.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
    toast.success("QR code downloaded");
  };

  return (
    <div className="App min-h-screen bg-[#050505]">
      <Navbar />
      <div className="mx-auto max-w-5xl px-5 py-10">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Dashboard</p>
          <h1 className="mt-2 font-display text-4xl font-black tracking-tight">
            Hey, {user.name?.split(" ")[0]}.
          </h1>
        </motion.div>

        {/* Tip link + stats bento */}
        <div className="mt-8 grid gap-4 md:grid-cols-3">
          <div className="rounded-2xl border border-white/10 bg-[#0F0F0F] p-6 md:col-span-3">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
              <Link2 className="h-4 w-4" /> Your tip link
            </div>
            <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center">
              <code
                data-testid="dashboard-tip-link"
                className="flex-1 truncate rounded-xl border border-white/10 bg-[#050505] px-4 py-3 font-mono-tip text-[#00E676]"
              >
                {tipUrl}
              </code>
              <div className="flex gap-2">
                <button
                  data-testid="copy-link-btn"
                  onClick={copyLink}
                  className="flex items-center gap-2 rounded-full bg-[#00E676] px-5 py-3 text-sm font-bold text-black active:scale-95"
                >
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  {copied ? "Copied" : "Copy"}
                </button>
                <a
                  href={tipUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="preview-link-btn"
                  className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-3 text-sm font-bold text-white hover:bg-white/5"
                >
                  <ExternalLink className="h-4 w-4" /> Preview
                </a>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-[#0F0F0F] p-6">
            <DollarSign className="h-6 w-6 text-[#00E676]" />
            <div data-testid="stat-total" className="mt-4 font-display text-4xl font-black">
              ${earnings ? earnings.total_received.toFixed(2) : "0.00"}
            </div>
            <p className="mt-1 text-sm text-zinc-500">Total tips received</p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-[#0F0F0F] p-6">
            <TrendingUp className="h-6 w-6 text-[#00E676]" />
            <div data-testid="stat-count" className="mt-4 font-display text-4xl font-black">
              {earnings ? earnings.tip_count : 0}
            </div>
            <p className="mt-1 text-sm text-zinc-500">Tips received</p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-[#0F0F0F] p-6">
            <div className="font-mono-tip text-xs text-zinc-500">@{user.username}</div>
            <div className="mt-4 font-display text-lg font-semibold text-zinc-300">
              {user.job_title || "Add a job title"}
            </div>
            <p className="mt-1 text-sm text-zinc-500">Your public identity</p>
          </div>
        </div>

        {/* Instant payouts (Stripe Connect) */}
        <ConnectCard />

        {/* Push notifications */}
        <PushNotifyCard />

        {/* Cash out */}
        <PayoutCard handles={user.handles} />

        {/* QR code */}
        <div className="mt-4 flex flex-col items-center gap-6 rounded-2xl border border-white/10 bg-[#0F0F0F] p-6 sm:flex-row sm:p-8">
          <div ref={qrRef} className="rounded-2xl bg-white p-4" data-testid="dashboard-qr">
            <QRCodeCanvas
              value={tipUrl}
              size={160}
              bgColor="#ffffff"
              fgColor="#050505"
              level="H"
              includeMargin={false}
            />
          </div>
          <div className="flex-1 text-center sm:text-left">
            <div className="flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 sm:justify-start">
              <QrCode className="h-4 w-4" /> Your tip QR code
            </div>
            <h3 className="mt-3 font-display text-xl font-semibold">Print it. Wear it. Get tipped.</h3>
            <p className="mt-2 leading-relaxed text-zinc-400">
              Put this on your name badge, tip jar, table tent or car window. Anyone can scan it to
              tip you instantly — no username typing needed.
            </p>
            <button
              data-testid="download-qr-btn"
              onClick={downloadQr}
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#00E676] px-6 py-3 text-sm font-bold text-black transition-transform active:scale-95"
            >
              <Download className="h-4 w-4" /> Download QR code
            </button>
          </div>
        </div>

        {/* Recent tips with notes */}
        {earnings?.tips?.length > 0 && (
          <div className="mt-4 rounded-2xl border border-white/10 bg-[#0F0F0F] p-6 sm:p-8" data-testid="recent-tips">
            <h2 className="font-display text-xl font-semibold">Recent tips</h2>
            <div className="mt-6 space-y-3">
              {earnings.tips.slice(0, 15).map((t, i) => (
                <div
                  key={t.id || i}
                  className="rounded-xl border border-white/10 bg-[#050505] px-4 py-3"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <span className="font-display text-lg font-bold text-[#00E676]">
                        ${Number(t.tip_amount).toFixed(2)}
                      </span>
                      {t.message ? (
                        <p className="mt-1 break-words text-sm italic text-zinc-300">“{t.message}”</p>
                      ) : (
                        <p className="mt-1 text-sm text-zinc-600">No note</p>
                      )}
                    </div>
                    {t.created_at && (
                      <span className="shrink-0 text-xs text-zinc-600">
                        {new Date(t.created_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>

                  {t.message && (
                    <div className="mt-3">
                      {t.reply && replyDrafts[t.id] === undefined ? (
                        <div className="flex items-start justify-between gap-3 border-l-2 border-[#00E676]/40 pl-3">
                          <div>
                            <p className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">Your reply</p>
                            <p className="mt-1 break-words text-sm text-zinc-400">{t.reply}</p>
                          </div>
                          <button
                            data-testid={`edit-reply-${t.id}`}
                            onClick={() => setReplyDrafts((d) => ({ ...d, [t.id]: t.reply }))}
                            className="shrink-0 text-xs font-semibold text-[#00E676]"
                          >
                            Edit
                          </button>
                        </div>
                      ) : replyDrafts[t.id] !== undefined ? (
                        <div className="flex items-center gap-2">
                          <input
                            data-testid={`reply-input-${t.id}`}
                            value={replyDrafts[t.id]}
                            onChange={(e) =>
                              setReplyDrafts((d) => ({ ...d, [t.id]: e.target.value.slice(0, 200) }))
                            }
                            placeholder="Say you're welcome…"
                            className="h-9 w-full rounded-lg border border-white/10 bg-[#0F0F0F] px-3 text-sm text-white placeholder:text-zinc-600 focus:border-[#00E676] focus:outline-none"
                          />
                          <button
                            data-testid={`suggest-reply-${t.id}`}
                            onClick={() => suggestReply(t.id)}
                            disabled={suggesting === t.id}
                            title="AI suggestion"
                            className="flex h-9 shrink-0 items-center gap-1 rounded-lg border border-[#00E676]/40 px-2.5 text-xs font-semibold text-[#00E676] active:scale-95 disabled:opacity-50"
                          >
                            {suggesting === t.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Sparkles className="h-4 w-4" />
                            )}
                          </button>
                          <button
                            data-testid={`save-reply-${t.id}`}
                            onClick={() => submitReply(t.id)}
                            className="shrink-0 rounded-lg bg-[#00E676] px-3 py-2 text-xs font-bold text-black active:scale-95"
                          >
                            Save
                          </button>
                        </div>
                      ) : (
                        <button
                          data-testid={`reply-btn-${t.id}`}
                          onClick={() => setReplyDrafts((d) => ({ ...d, [t.id]: "" }))}
                          className="text-xs font-semibold text-zinc-400 hover:text-[#00E676]"
                        >
                          + Reply publicly
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Profile editor */}
        <div className="mt-8 grid gap-4 lg:grid-cols-2">
          <div className="rounded-2xl border border-white/10 bg-[#0F0F0F] p-6 sm:p-8">
            <h2 className="font-display text-xl font-semibold">Profile</h2>
            <div className="mt-6 space-y-5">
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">Name</Label>
                <Input
                  data-testid="profile-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-2 h-11 border-white/10 bg-[#050505] text-white focus-visible:ring-[#00E676]"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">Job title</Label>
                <Input
                  data-testid="profile-job-input"
                  value={form.job_title}
                  onChange={(e) => setForm({ ...form, job_title: e.target.value })}
                  placeholder="Valet Attendant"
                  className="mt-2 h-11 border-white/10 bg-[#050505] text-white focus-visible:ring-[#00E676]"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">Avatar URL</Label>
                <Input
                  data-testid="profile-avatar-input"
                  value={form.avatar_url}
                  onChange={(e) => setForm({ ...form, avatar_url: e.target.value })}
                  placeholder="https://..."
                  className="mt-2 h-11 border-white/10 bg-[#050505] text-white focus-visible:ring-[#00E676]"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">Phone (for tip alerts via SMS)</Label>
                <Input
                  data-testid="profile-phone-input"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+15552104488"
                  className="mt-2 h-11 border-white/10 bg-[#050505] text-white focus-visible:ring-[#00E676]"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">Bio</Label>
                <Textarea
                  data-testid="profile-bio-input"
                  value={form.bio}
                  onChange={(e) => setForm({ ...form, bio: e.target.value })}
                  className="mt-2 border-white/10 bg-[#050505] text-white focus-visible:ring-[#00E676]"
                />
              </div>
              <button
                type="button"
                data-testid="public-wall-toggle"
                onClick={() => setForm({ ...form, public_wall: !form.public_wall })}
                className="flex w-full items-center justify-between rounded-xl border border-white/10 bg-[#050505] px-4 py-3 text-left"
              >
                <span>
                  <span className="block text-sm font-semibold text-white">Public tips wall</span>
                  <span className="block text-xs text-zinc-500">Show recent tip notes on your public page</span>
                </span>
                <span
                  className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${
                    form.public_wall ? "bg-[#00E676]" : "bg-zinc-700"
                  }`}
                >
                  <span
                    className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${
                      form.public_wall ? "translate-x-[22px]" : "translate-x-0.5"
                    }`}
                  />
                </span>
              </button>
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-[#0F0F0F] p-6 sm:p-8">
            <h2 className="font-display text-xl font-semibold">Payment handles</h2>
            <p className="mt-1 text-sm text-zinc-500">
              Used to pay out the tips you receive.
            </p>
            <div className="mt-6 space-y-5">
              <div>
                <Label className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#008CFF]">
                  <VenmoLogo className="h-4 w-4" /> Venmo username
                </Label>
                <Input
                  data-testid="handle-venmo-input"
                  value={form.handles.venmo}
                  onChange={setHandle("venmo")}
                  placeholder="marcus-reeves"
                  className="mt-2 h-11 border-white/10 bg-[#050505] text-white focus-visible:ring-[#00E676]"
                />
              </div>
              <div>
                <Label className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#0079C1]">
                  <PayPalLogo className="h-4 w-4" /> PayPal.me name
                </Label>
                <Input
                  data-testid="handle-paypal-input"
                  value={form.handles.paypal}
                  onChange={setHandle("paypal")}
                  placeholder="marcusreeves"
                  className="mt-2 h-11 border-white/10 bg-[#050505] text-white focus-visible:ring-[#00E676]"
                />
              </div>
              <div>
                <Label className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-white">
                  <AppleLogo className="h-4 w-4" /> Apple Cash (phone/email)
                </Label>
                <Input
                  data-testid="handle-applepay-input"
                  value={form.handles.applepay}
                  onChange={setHandle("applepay")}
                  placeholder="(555) 210-4488"
                  className="mt-2 h-11 border-white/10 bg-[#050505] text-white focus-visible:ring-[#00E676]"
                />
              </div>
            </div>
          </div>
        </div>

        <button
          data-testid="save-profile-btn"
          onClick={save}
          disabled={saving}
          className="mt-6 flex h-12 items-center justify-center rounded-full bg-[#00E676] px-10 font-bold text-black transition-transform active:scale-95 disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "Save changes"}
        </button>
      </div>
    </div>
  );
}
